/**
 * Block registry.
 *
 * Adding a new block type:
 *   1. Add a schema entry to BLOCK_SCHEMAS below.
 *   2. Add a renderer in ./renderers/<type>.jsx and register it in BlockRenderer.jsx.
 *
 * Field types supported by the admin editor:
 *   text       — single-line input
 *   textarea   — multi-line input
 *   image      — Media Library picker (URL string)
 *   list       — array of strings (bullet points)
 *   select     — dropdown; options from `options: [{label, value}]`
 *
 * Every schema also defines a `defaults` shape used when creating a new block.
 */

export const BLOCK_SCHEMAS = {
  hero: {
    label: "Hero",
    description: "Full-width centered heading, accent word, and subtitle.",
    defaults: {
      eyebrow: "",
      title: "New hero title",
      title_accent: "",
      subtitle: "",
    },
    fields: [
      { key: "eyebrow", label: "Eyebrow (small pill above title)", type: "text" },
      { key: "title", label: "Title (main text)", type: "text" },
      { key: "title_accent", label: "Title accent (blue word)", type: "text" },
      { key: "subtitle", label: "Subtitle (rich text)", type: "richtext" },
    ],
  },

  hero_split: {
    label: "Hero — split (image + copy + buttons)",
    description: "Two-column hero: copy + buttons on the left, image on the right. Button links can point to real routes (/contact) or same-page anchors (#openings).",
    defaults: {
      title: "New title",
      title_accent: "",
      subtitle: "",
      image: "",
      primary_label: "",
      primary_href: "",
      secondary_label: "",
      secondary_href: "",
    },
    fields: [
      { key: "title", label: "Title (main text)", type: "text" },
      { key: "title_accent", label: "Title accent (blue word)", type: "text" },
      { key: "subtitle", label: "Subtitle (rich text)", type: "richtext" },
      { key: "image", label: "Image", type: "image" },
      { key: "primary_label", label: "Primary button label", type: "text" },
      { key: "primary_href", label: "Primary button link (route or #anchor)", type: "text" },
      { key: "secondary_label", label: "Secondary button label", type: "text" },
      { key: "secondary_href", label: "Secondary button link (route or #anchor)", type: "text" },
    ],
  },

  features_grid: {
    label: "Features Grid (title + description cards)",
    description: "Heading + optional intro + card grid (up to 4 per row). Each card has a title and description. Great for values, benefits, feature callouts.",
    defaults: {
      heading: "New section",
      intro: "",
      items: [],
    },
    fields: [
      { key: "heading", label: "Heading", type: "text" },
      { key: "intro", label: "Intro paragraph (optional)", type: "textarea" },
      {
        key: "items",
        label: "Cards",
        type: "object_list",
        item_fields: [
          { key: "title", label: "Card title", type: "text" },
          { key: "description", label: "Card description", type: "textarea" },
        ],
      },
    ],
  },

  two_column: {
    label: "Two-column (image + text + feature list)",
    description: "Section with an image on one side and headline / body / bullet features + CTAs on the other.",
    defaults: {
      title: "New section",
      body: "",
      features: [],
      image: "",
      image_side: "right",
      primary_label: "Request a Demo",
      primary_href: "/contact",
      secondary_label: "",
      secondary_href: "",
    },
    fields: [
      { key: "title", label: "Section title", type: "text" },
      { key: "body", label: "Body / tagline (rich text)", type: "richtext" },
      { key: "features", label: "Feature bullets", type: "list" },
      { key: "image", label: "Image", type: "image" },
      {
        key: "image_side",
        label: "Image position",
        type: "select",
        options: [
          { label: "Right", value: "right" },
          { label: "Left", value: "left" },
        ],
      },
      { key: "primary_label", label: "Primary button label", type: "text" },
      { key: "primary_href", label: "Primary button link", type: "text" },
      { key: "secondary_label", label: "Secondary button label", type: "text" },
      { key: "secondary_href", label: "Secondary button link", type: "text" },
    ],
  },

  cta_banner: {
    label: "CTA Banner",
    description: "Full-width banner with heading, body, and two buttons. Choose light (sky-blue) or dark background.",
    defaults: {
      heading: "Ready to see it in action?",
      body: "",
      primary_label: "Request a Demo",
      primary_href: "/contact",
      secondary_label: "Contact Sales",
      secondary_href: "/contact",
      style: "light",
    },
    fields: [
      { key: "heading", label: "Heading", type: "text" },
      { key: "body", label: "Body (rich text)", type: "richtext" },
      {
        key: "style",
        label: "Background style",
        type: "select",
        options: [
          { label: "Light (sky-blue)", value: "light" },
          { label: "Dark (slate-900)", value: "dark" },
        ],
      },
      { key: "primary_label", label: "Primary button label", type: "text" },
      { key: "primary_href", label: "Primary button link", type: "text" },
      { key: "secondary_label", label: "Secondary button label", type: "text" },
      { key: "secondary_href", label: "Secondary button link", type: "text" },
    ],
  },

  text: {
    label: "Text",
    description: "Heading plus a rich-text body (bold, links, bullets, headings).",
    defaults: {
      heading: "New section",
      body: "",
      alignment: "left",
    },
    fields: [
      { key: "heading", label: "Heading", type: "text" },
      { key: "body", label: "Body (rich text)", type: "richtext" },
      {
        key: "alignment",
        label: "Alignment",
        type: "select",
        options: [
          { label: "Left", value: "left" },
          { label: "Center", value: "center" },
        ],
      },
    ],
  },

  image: {
    label: "Image (full width)",
    description: "Single image displayed centered with configurable container width. Great for large diagrams, plans/pricing charts, product screenshots. Choose the width that best matches your image's dimensions.",
    defaults: {
      image: "",
      alt: "",
      caption: "",
      container_size: "lg",
      rounded: "xl",
      shadow: true,
      background: "white",
      href: "",
      top_spacing: "normal",
      bottom_spacing: "normal",
    },
    fields: [
      { key: "image", label: "Image", type: "image" },
      { key: "alt", label: "Alt text (accessibility + SEO)", type: "text" },
      { key: "caption", label: "Caption (optional, shown below image)", type: "text" },
      {
        key: "container_size",
        label: "Image width",
        type: "select",
        options: [
          { label: "Small (fits blog width)", value: "sm" },
          { label: "Medium", value: "md" },
          { label: "Large (matches Integrations page — recommended)", value: "lg" },
          { label: "Extra Large", value: "xl" },
          { label: "Full width (edge to edge)", value: "full" },
        ],
      },
      {
        key: "rounded",
        label: "Corner rounding",
        type: "select",
        options: [
          { label: "None (sharp corners)", value: "none" },
          { label: "Small", value: "md" },
          { label: "Medium", value: "lg" },
          { label: "Large (recommended)", value: "xl" },
          { label: "Extra Large", value: "2xl" },
        ],
      },
      { key: "shadow", label: "Drop shadow", type: "boolean" },
      {
        key: "background",
        label: "Section background",
        type: "select",
        options: [
          { label: "White", value: "white" },
          { label: "Light slate", value: "slate" },
        ],
      },
      {
        key: "top_spacing",
        label: "Space above image",
        type: "select",
        options: [
          { label: "Normal", value: "normal" },
          { label: "Tight", value: "tight" },
          { label: "Attach to section above (recommended when image follows a hero)", value: "attach" },
        ],
      },
      {
        key: "bottom_spacing",
        label: "Space below image",
        type: "select",
        options: [
          { label: "Normal", value: "normal" },
          { label: "Tight", value: "tight" },
          { label: "None", value: "none" },
        ],
      },
      { key: "href", label: "Make image clickable (optional URL)", type: "text" },
    ],
  },

  // ---------------------------------------------------------------------------
  // Homepage-specific custom blocks
  // ---------------------------------------------------------------------------
  hero_overlay_image: {
    label: "Hero — image + copy + video (homepage style)",
    description:
      "Full homepage hero: badge, tri-part headline, intro, tagline pill row, primary/secondary CTAs, optional Watch-Video button, and right-hand image with optional clickable overlay.",
    defaults: {
      badge_text: "",
      headline_lead: "",
      headline_highlight: "",
      headline_trail: "",
      intro: "",
      tagline: "",
      cta_primary_label: "Request a Demo",
      cta_primary_href: "/contact",
      cta_secondary_label: "",
      cta_secondary_href: "",
      video_url: "",
      video_label: "Watch Video",
      image: "",
      image_alt: "",
      image_href: "",
    },
    fields: [
      { key: "badge_text", label: "Badge text (small pill above headline)", type: "text" },
      { key: "headline_lead", label: "Headline — lead words", type: "text" },
      { key: "headline_highlight", label: "Headline — accent (blue) words", type: "text" },
      { key: "headline_trail", label: "Headline — trailing words", type: "text" },
      { key: "intro", label: "Intro paragraph (rich text)", type: "richtext" },
      {
        key: "tagline",
        label:
          "Tagline (auto-renders as pill row when it matches '18 Years Serving the X, Y, and Z Industries', or as checkmark grid for 3-6 comma-separated items)",
        type: "text",
      },
      { key: "cta_primary_label", label: "Primary button label", type: "text" },
      { key: "cta_primary_href", label: "Primary button link", type: "text" },
      { key: "cta_secondary_label", label: "Secondary button label (blank = hidden)", type: "text" },
      { key: "cta_secondary_href", label: "Secondary button link", type: "text" },
      { key: "video_url", label: "Watch-Video URL (blank = hidden)", type: "text" },
      { key: "video_label", label: "Watch-Video button label", type: "text" },
      { key: "image", label: "Right-hand image", type: "image" },
      { key: "image_alt", label: "Image alt text (accessibility)", type: "text" },
      { key: "image_href", label: "Optional link when image is clicked (route)", type: "text" },
    ],
  },

  testimonials_rotator: {
    label: "Testimonials — auto-rotating carousel",
    description:
      "Auto-rotating testimonial carousel with prev/next arrows and dot indicators. Each item has a quote, author, company, and optional logo image.",
    defaults: {
      eyebrow: "In Their Own Words",
      heading: "What our customers are saying",
      intro: "",
      items: [],
    },
    fields: [
      { key: "eyebrow", label: "Eyebrow (small text above heading)", type: "text" },
      { key: "heading", label: "Section heading", type: "text" },
      { key: "intro", label: "Intro paragraph (optional)", type: "textarea" },
      {
        key: "items",
        label: "Testimonials",
        type: "object_list",
        item_fields: [
          { key: "quote", label: "Quote", type: "textarea" },
          { key: "author", label: "Author name", type: "text" },
          { key: "company", label: "Company", type: "text" },
          { key: "logo", label: "Logo image URL or /path (optional)", type: "text" },
        ],
      },
    ],
  },

  workflow_engine: {
    label: "Workflow Engine — numbered stages + benefits",
    description:
      "Editable Dynamic-Workflow-Engine section with eyebrow, heading, subtitle, numbered stage cards (icon + title + description), a footer callout with CTA, and a benefit strip.",
    defaults: {
      eyebrow: "Dynamic Workflow Engine",
      heading: "Workflows That Work for You",
      subtitle: "",
      card_intro: "",
      stages: [],
      footer_title: "Built for Your Business",
      footer_body: "",
      footer_cta_label: "Learn how it works",
      footer_cta_href: "/advance",
      benefits: [],
    },
    fields: [
      { key: "eyebrow", label: "Eyebrow", type: "text" },
      { key: "heading", label: "Heading", type: "text" },
      { key: "subtitle", label: "Subtitle", type: "textarea" },
      { key: "card_intro", label: "Card intro line (inside sky card)", type: "textarea" },
      {
        key: "stages",
        label: "Stages (numbered automatically)",
        type: "object_list",
        item_fields: [
          { key: "title", label: "Stage title", type: "text" },
          { key: "description", label: "Stage description", type: "textarea" },
          {
            key: "icon",
            label: "Icon",
            type: "select",
            options: [
              { label: "FileText (Quote)", value: "FileText" },
              { label: "UserCheck (Approval)", value: "UserCheck" },
              { label: "PencilRuler (Artwork)", value: "PencilRuler" },
              { label: "ShoppingCart (PO)", value: "ShoppingCart" },
              { label: "Settings2 (Production)", value: "Settings2" },
              { label: "Truck (Shipping)", value: "Truck" },
              { label: "Receipt (Invoice)", value: "Receipt" },
              { label: "Landmark (Accounting)", value: "Landmark" },
              { label: "Timer (Save Time)", value: "Timer" },
              { label: "Target (Accuracy)", value: "Target" },
              { label: "Eye (Visibility)", value: "Eye" },
              { label: "TrendingUp (Profit)", value: "TrendingUp" },
              { label: "Sparkles", value: "Sparkles" },
              { label: "Zap", value: "Zap" },
              { label: "Users", value: "Users" },
              { label: "BarChart2", value: "BarChart2" },
            ],
          },
          {
            key: "color",
            label: "Color theme",
            type: "select",
            options: [
              { label: "Sky", value: "sky" },
              { label: "Violet", value: "violet" },
              { label: "Emerald", value: "emerald" },
              { label: "Amber", value: "amber" },
            ],
          },
        ],
      },
      { key: "footer_title", label: "Footer callout title", type: "text" },
      { key: "footer_body", label: "Footer callout body", type: "textarea" },
      { key: "footer_cta_label", label: "Footer CTA label", type: "text" },
      { key: "footer_cta_href", label: "Footer CTA link", type: "text" },
      {
        key: "benefits",
        label: "Benefit strip (below the card)",
        type: "object_list",
        item_fields: [
          { key: "title", label: "Benefit title", type: "text" },
          { key: "description", label: "Benefit description", type: "textarea" },
          {
            key: "icon",
            label: "Icon",
            type: "select",
            options: [
              { label: "Timer", value: "Timer" },
              { label: "Target", value: "Target" },
              { label: "Eye", value: "Eye" },
              { label: "TrendingUp", value: "TrendingUp" },
              { label: "Sparkles", value: "Sparkles" },
              { label: "Zap", value: "Zap" },
              { label: "Users", value: "Users" },
              { label: "BarChart2", value: "BarChart2" },
            ],
          },
          {
            key: "color",
            label: "Color theme",
            type: "select",
            options: [
              { label: "Sky", value: "sky" },
              { label: "Violet", value: "violet" },
              { label: "Emerald", value: "emerald" },
              { label: "Amber", value: "amber" },
            ],
          },
        ],
      },
    ],
  },

  // ---------------------------------------------------------------------------
  // Widget wrapper blocks. These render existing components that already
  // source their copy from Site Settings. They exist purely to give admins a
  // slot in the block editor so sections can be reordered / toggled / removed.
  // Editing the underlying copy is still done from the Site Settings screen.
  // ---------------------------------------------------------------------------
  partner_logos: {
    label: "Elite Suppliers logo marquee",
    description:
      "Fully editable eyebrow, heading (with optional link), subtitle, CTA button, and a repeatable list of supplier logos. Each logo has a name, an optional brand URL (internal or external), and a logo image URL. Renders as an auto-scrolling marquee.",
    defaults: {
      eyebrow: "The Elite Supplier Program",
      heading: "Antera's Elite Suppliers",
      heading_href: "/exchange/elite-supplier",
      subtitle:
        "Preferred supplier partners delivering deep PromoStandards integration, real-time data, and exclusive commercial benefits to the Antera community.",
      cta_label: "Learn about the program",
      cta_href: "/exchange/elite-supplier",
      marquee_speed: 35,
      logos: [],
    },
    fields: [
      { key: "eyebrow", label: "Eyebrow (small pill above heading)", type: "text" },
      { key: "heading", label: "Heading", type: "text" },
      {
        key: "heading_href",
        label: "Heading link (internal route or full https URL, blank = no link)",
        type: "text",
      },
      { key: "subtitle", label: "Subtitle paragraph", type: "textarea" },
      { key: "cta_label", label: "CTA button label (blank = hidden)", type: "text" },
      { key: "cta_href", label: "CTA button link", type: "text" },
      {
        key: "marquee_speed",
        label: "Marquee scroll speed (higher = faster, default 35)",
        type: "text",
      },
      {
        key: "logos",
        label: "Supplier logos",
        type: "object_list",
        item_fields: [
          { key: "name", label: "Supplier name (used for alt-text)", type: "text" },
          {
            key: "brand_url",
            label: "Brand URL (optional — makes the logo clickable)",
            type: "text",
          },
          { key: "logo_url", label: "Logo image", type: "image" },
        ],
      },
    ],
  },
  solutions_overview: {
    label: "Solutions Overview (icon grid)",
    description:
      "Homepage 'Solutions for every role' section — heading + intro, 6 industry cards (title/desc/link) with position-locked icons, and a 'View all solutions' link. Icons and gradient accents stay fixed to their card slot.",
    defaults: {
      heading: "Built for your industry",
      intro: "",
      cards: [],
      view_all_label: "View all solutions",
    },
    fields: [
      { key: "heading", label: "Section heading", type: "text" },
      { key: "intro", label: "Intro paragraph", type: "textarea" },
      { key: "view_all_label", label: "View-all link label (below the grid)", type: "text" },
      {
        key: "cards",
        label: "Industry cards (up to 6 — icons position-locked to slot order: Distributors, Suppliers, Decorators, Print Shops, Sign Companies, QuickBooks)",
        type: "object_list",
        item_fields: [
          { key: "title", label: "Card title", type: "text" },
          { key: "desc", label: "Card description", type: "textarea" },
          { key: "link", label: "Card link (route)", type: "text" },
        ],
      },
    ],
  },
  power_every_part: {
    label: "Power Every Part of Your Business",
    description:
      "The 4-column pillar showcase (Sell / Operate / Get Paid / Grow). Eyebrow, heading, subtitle, 4 pillar cards (title + tagline + ordered feature items), and a bottom CTA banner. Pillar palette and icons stay position-locked; feature-item icons follow slot order within each pillar.",
    defaults: {
      eyebrow: "Everything you need to run your business",
      heading: "Power every part of your business",
      subtitle: "",
      pillars: [],
      cta_title: "One Connected Platform.",
      cta_subtitle: "All the tools. All the data. All in sync.",
      cta_label: "Explore All Features",
      cta_href: "/solutions/advance",
    },
    fields: [
      { key: "eyebrow", label: "Eyebrow (small text above heading)", type: "text" },
      { key: "heading", label: "Heading", type: "text" },
      { key: "subtitle", label: "Subtitle paragraph", type: "textarea" },
      {
        key: "pillars",
        label: "Pillar cards (4 — palette and icons position-locked to slot order: Sell/emerald, Operate/sky, Get Paid/amber, Grow/violet)",
        type: "object_list",
        item_fields: [
          { key: "title",   label: "Pillar title (e.g. Sell)", type: "text" },
          { key: "tagline", label: "Pillar tagline", type: "textarea" },
          {
            key: "items",
            label: "Feature items (each row = one capability label)",
            type: "object_list",
            item_fields: [
              { key: "label", label: "Capability label (e.g. CRM, Orders)", type: "text" },
            ],
          },
        ],
      },
      { key: "cta_title", label: "Bottom CTA banner — title", type: "text" },
      { key: "cta_subtitle", label: "Bottom CTA banner — subtitle", type: "text" },
      { key: "cta_label", label: "Bottom CTA button label (blank = hide button)", type: "text" },
      { key: "cta_href", label: "Bottom CTA button link", type: "text" },
    ],
  },
  home_features_grid: {
    label: "Homepage Features Grid (10-step workflow)",
    description:
      "The 10-step 'Every Department. Every Step. One Source of Truth.' workflow row plus a 5-item benefit strip. Icons and per-step colours are position-locked to slot order.",
    defaults: {
      eyebrow: "One Connected Workflow",
      heading: "Every Department. Every Step. One Source of Truth.",
      intro: "",
      steps: [],
      benefits: [],
    },
    fields: [
      { key: "eyebrow", label: "Eyebrow (small text above heading)", type: "text" },
      { key: "heading", label: "Heading", type: "text" },
      { key: "intro", label: "Intro paragraph", type: "textarea" },
      {
        key: "steps",
        label: "Workflow steps (up to 10 — icons/colours position-locked: CRM, Presentations, Quotes, Orders, Artwork, Production, Shipping, Invoices, Accounting, AI Insights)",
        type: "object_list",
        item_fields: [
          { key: "title", label: "Step title", type: "text" },
          { key: "desc", label: "Step description", type: "text" },
        ],
      },
      {
        key: "benefits",
        label: "Benefit strip (up to 5)",
        type: "object_list",
        item_fields: [
          { key: "title", label: "Benefit title", type: "text" },
          { key: "desc", label: "Benefit description", type: "text" },
        ],
      },
    ],
  },
  products_showcase: {
    label: "Products Showcase (Advance / Aether / Integrations)",
    description:
      "Left column with 3 product cards (title + desc + link) + right-side hero image. Icons and per-card accent colours are position-locked to slot order (Advance/sky, Aether/indigo, Integrations/emerald).",
    defaults: {
      heading: "Our Products",
      intro: "",
      cards: [],
      image_url: "",
    },
    fields: [
      { key: "heading", label: "Heading", type: "text" },
      { key: "intro", label: "Intro paragraph", type: "textarea" },
      { key: "image_url", label: "Right-side hero image", type: "image" },
      {
        key: "cards",
        label: "Product cards (up to 3 — icons/accent position-locked to slot order)",
        type: "object_list",
        item_fields: [
          { key: "title", label: "Card title", type: "text" },
          { key: "desc", label: "Card description", type: "textarea" },
          { key: "link", label: "Card link (route)", type: "text" },
        ],
      },
    ],
  },
  get_started_now: {
    label: "Get Started Now (6-step onboarding)",
    description:
      "6-step onboarding timeline + 5-item benefit strip + trust line. Icons and per-step colour tone (light → mid → dark → night) are position-locked to slot order.",
    defaults: {
      eyebrow: "Getting started is easy",
      heading_lead: "Get Started",
      heading_trail: "Now",
      subtitle: "",
      steps: [],
      benefits: [],
      trust_line: "",
    },
    fields: [
      { key: "eyebrow", label: "Eyebrow (small text above heading)", type: "text" },
      { key: "heading_lead", label: "Heading — lead (blue) words", type: "text" },
      { key: "heading_trail", label: "Heading — trailing (dark) words", type: "text" },
      { key: "subtitle", label: "Subtitle paragraph", type: "textarea" },
      {
        key: "steps",
        label: "Onboarding steps (up to 6 — icons/tone position-locked: Schedule Demo, Implementation, Training, Data Import, Go Live, Customer Success)",
        type: "object_list",
        item_fields: [
          { key: "title", label: "Step title", type: "text" },
          { key: "desc", label: "Step description", type: "textarea" },
        ],
      },
      {
        key: "benefits",
        label: "Benefit strip (up to 5 — icons position-locked)",
        type: "object_list",
        item_fields: [
          { key: "title", label: "Benefit title", type: "text" },
          { key: "desc", label: "Benefit description", type: "text" },
        ],
      },
      { key: "trust_line", label: "Trust banner text below benefits (blank = hidden)", type: "text" },
    ],
  },
  final_cta: {
    label: "Final page-end CTA (dark)",
    description:
      "Full-width dark 'Ready to see it in action?' CTA — heading, intro, primary and secondary buttons. Uses a slate-900 background with sky-500 primary button.",
    defaults: {
      heading: "Scale with Confidence",
      intro: "",
      primary_cta_label: "Request Demo",
      primary_cta_href: "/contact",
      secondary_cta_label: "Why Antera?",
      secondary_cta_href: "/why",
    },
    fields: [
      { key: "heading", label: "Heading", type: "text" },
      { key: "intro", label: "Intro paragraph", type: "textarea" },
      { key: "primary_cta_label", label: "Primary button label", type: "text" },
      { key: "primary_cta_href", label: "Primary button link", type: "text" },
      { key: "secondary_cta_label", label: "Secondary button label", type: "text" },
      { key: "secondary_cta_href", label: "Secondary button link", type: "text" },
    ],
  },
  why_antera: {
    label: "Why Antera — cards + trust bullets",
    description:
      "Fully editable 'Why Antera' section: eyebrow, tri-part headline (lead + accent), intro, 4 feature cards (each with icon + title + body), a row of trust bullets, and a final CTA.",
    defaults: {
      eyebrow: "Why Antera",
      heading_lead: "Built for Today.",
      heading_highlight: "Engineered for Tomorrow.",
      intro: "",
      cards: [],
      trust_bullets: [],
      cta_label: "Request Demo",
      cta_href: "/contact",
    },
    fields: [
      { key: "eyebrow", label: "Eyebrow (small text above heading)", type: "text" },
      { key: "heading_lead", label: "Heading — lead words", type: "text" },
      { key: "heading_highlight", label: "Heading — accent (gradient) words", type: "text" },
      { key: "intro", label: "Intro paragraph", type: "textarea" },
      {
        key: "cards",
        label: "Feature cards",
        type: "object_list",
        item_fields: [
          { key: "title", label: "Card title", type: "text" },
          { key: "body", label: "Card body", type: "textarea" },
          {
            key: "icon",
            label: "Icon",
            type: "select",
            options: [
              { label: "Advanced Technology (cloud + lightning)", value: "tech" },
              { label: "Continuous Innovation (arrows + node)", value: "innovation" },
              { label: "AI (chip / neural net)", value: "ai" },
              { label: "Presentation Builder (screen)", value: "presentation" },
            ],
          },
        ],
      },
      {
        key: "trust_bullets",
        label: "Trust bullets (row of checkmarks)",
        type: "object_list",
        item_fields: [
          { key: "text", label: "Bullet text", type: "text" },
        ],
      },
      { key: "cta_label", label: "CTA button label (blank = hidden)", type: "text" },
      { key: "cta_href", label: "CTA button link", type: "text" },
    ],
  },
  success_story: {
    label: "Success Story spotlight",
    description:
      "Fully editable customer success spotlight: badge, tri-part headline (lead + accent), achievements list, author (name + title + avatar), main image with floating stat card, and CTA button.",
    defaults: {
      badge: "Real Customer Results",
      headline_lead: "",
      headline_highlight: "",
      achievements: [],
      author_name: "",
      author_title: "",
      author_avatar: "",
      main_image: "",
      main_image_alt: "",
      stat_number: "",
      stat_label: "",
      cta_label: "Read the Full Story",
      cta_href: "/case-studies",
    },
    fields: [
      { key: "badge", label: "Badge pill (above headline)", type: "text" },
      { key: "headline_lead", label: "Headline — lead words", type: "text" },
      {
        key: "headline_highlight",
        label: "Headline — accent (blue italic) words",
        type: "text",
      },
      {
        key: "achievements",
        label: "Achievement bullets",
        type: "object_list",
        item_fields: [
          { key: "text", label: "Achievement text", type: "textarea" },
        ],
      },
      { key: "author_name", label: "Author name", type: "text" },
      { key: "author_title", label: "Author title / company", type: "text" },
      { key: "author_avatar", label: "Author avatar image", type: "image" },
      { key: "main_image", label: "Main image (right side)", type: "image" },
      { key: "main_image_alt", label: "Main image alt text", type: "text" },
      { key: "stat_number", label: "Floating stat — big number (e.g. +40%)", type: "text" },
      { key: "stat_label", label: "Floating stat — label (e.g. Faster Processing)", type: "text" },
      { key: "cta_label", label: "CTA button label (blank = hidden)", type: "text" },
      { key: "cta_href", label: "CTA button link", type: "text" },
    ],
  },
  home_cta_dark: {
    label: "Widget — Dark CTA banner (sky-blue)",
    description:
      "The dark sky-blue CTA strip used between homepage sections. Copy is edited in Site Settings → Homepage → CTA Banner (Dark).",
    defaults: {},
    fields: [],
  },
  home_cta_light: {
    label: "Widget — Light CTA banner (slate)",
    description:
      "The lighter slate CTA strip used between homepage sections. Copy is edited in Site Settings → Homepage → CTA Banner (Light).",
    defaults: {},
    fields: [],
  },
  contact_form: {
    label: "Widget — Contact / Demo request form",
    description:
      "Renders the full contact info panel + demo-request form. Copy is edited in Site Settings → Contact.",
    defaults: {},
    fields: [],
  },

  solution_detail_page: {
    label: "Solution / Product Detail Page (composite)",
    description:
      "Renders the full solution-detail layout (breadcrumb, hero, optional stats band, optional workflow strip, alternating detail sections with a sticky lead-capture sidebar, optional why-choose grid, dark-sky final CTA, and a related-insights carousel). One block per page. Used by /advance and /aether.",
    defaults: {
      title: "Solution title",      tagline: "",
      image: "",
      cta_label: "Schedule Your Personalized Demo",
      parent: { name: "Products", path: "/products" },
      stats: { eyebrow: "", heading: "", intro: "", items: [], footer_note: "" },
      workflow: { eyebrow: "", heading: "", intro: "", steps: [] },
      sections: [],
      why_choose: { eyebrow: "", heading: "", intro: "", cards: [] },
      final_cta: { heading: "", subheading: "", button_label: "Request a Demo" },
      relatedTag: "",
      relatedTitle: "",
    },
    fields: [
      { key: "title", label: "Hero title", type: "text" },
      { key: "tagline", label: "Hero tagline (paragraph under title)", type: "textarea" },
      { key: "image", label: "Hero image", type: "image" },
      { key: "cta_label", label: "Sticky lead-form CTA label", type: "text" },
      { key: "relatedTag", label: "Related insights — tag filter", type: "text" },
      { key: "relatedTitle", label: "Related insights — carousel heading", type: "text" },
      {
        key: "parent",
        label: "Breadcrumb parent (link back to)",
        type: "group",
        subfields: [
          { key: "name", label: "Parent name (e.g. Products, Solutions)", type: "text" },
          { key: "path", label: "Parent path (e.g. /products)", type: "text" },
        ],
      },
      {
        key: "stats",
        label: "Stats band (leave every field empty to hide)",
        type: "group",
        subfields: [
          { key: "eyebrow", label: "Eyebrow (small caps text above heading)", type: "text" },
          { key: "heading", label: "Heading", type: "text" },
          { key: "intro", label: "Intro paragraph", type: "textarea" },
          {
            key: "items",
            label: "Stat items (icons: trending-up, dollar-sign, clock, users, chart-bar, target, check)",
            type: "object_list",
            item_fields: [
              { key: "icon",   label: "Icon", type: "select",
                options: ["trending-up", "dollar-sign", "clock", "users", "chart-bar", "target", "check"] },
              { key: "value",  label: "Value (number or short text, e.g. 40)", type: "text" },
              { key: "suffix", label: "Suffix (e.g. %, x, hrs — leave blank if none)", type: "text" },
              { key: "label",  label: "Label under the value", type: "text" },
            ],
          },
          { key: "footer_note", label: "Footer note (fine print under stats)", type: "textarea" },
        ],
      },
      {
        key: "workflow",
        label: "Workflow strip (leave every field empty to hide)",
        type: "group",
        subfields: [
          { key: "eyebrow", label: "Eyebrow", type: "text" },
          { key: "heading", label: "Heading", type: "text" },
          { key: "intro", label: "Intro paragraph", type: "textarea" },
          {
            key: "steps",
            label: "Workflow steps",
            type: "object_list",
            item_fields: [
              { key: "title", label: "Step title", type: "text" },
            ],
          },
        ],
      },
      {
        key: "sections",
        label: "Detail sections (renders in the left column next to the sticky form)",
        type: "object_list",
        item_fields: [
          { key: "heading", label: "Section heading", type: "text" },
          {
            key: "points",
            label: "Bullet points",
            type: "object_list",
            item_fields: [
              { key: "title", label: "Point title", type: "text" },
              { key: "desc",  label: "Point description", type: "textarea" },
            ],
          },
        ],
      },
      {
        key: "why_choose",
        label: "Why-choose grid (leave every field empty to hide)",
        type: "group",
        subfields: [
          { key: "eyebrow", label: "Eyebrow", type: "text" },
          { key: "heading", label: "Heading", type: "text" },
          { key: "intro", label: "Intro paragraph", type: "textarea" },
          {
            key: "cards",
            label: "Cards",
            type: "object_list",
            item_fields: [
              { key: "title", label: "Card title", type: "text" },
              { key: "desc",  label: "Card description", type: "textarea" },
            ],
          },
        ],
      },
      {
        key: "final_cta",
        label: "Final dark-sky CTA banner",
        type: "group",
        subfields: [
          { key: "heading",       label: "Heading", type: "text" },
          { key: "subheading",    label: "Sub-heading (paragraph under heading)", type: "textarea" },
          { key: "button_label",  label: "Button label", type: "text" },
        ],
      },
    ],
  },

  user_groups_page: {
    label: "User Groups Page (composite)",
    description:
      "Renders the /exchange/user-groups page: breadcrumb, hero (prefix + accent title + tagline), 4 feature cards in a 2×2 grid, and a centered CTA button.",
    defaults: {
      breadcrumb_label: "Antera Exchange",
      breadcrumb_href: "/exchange",
      title_prefix: "Product",
      title_accent: "User Groups",
      tagline: "",
      feature_cards: [],
      cta_label: "Join a User Group",
      cta_href: "/contact",
    },
    fields: [
      { key: "breadcrumb_label", label: "Breadcrumb label (leave empty to hide)", type: "text" },
      { key: "breadcrumb_href", label: "Breadcrumb link target", type: "text" },
      { key: "title_prefix", label: "Hero title prefix (before the accent word)", type: "text" },
      { key: "title_accent", label: "Hero title accent (highlighted in sky-600)", type: "text" },
      { key: "tagline", label: "Hero tagline paragraph", type: "textarea" },
      { key: "cta_label", label: "Bottom CTA button label", type: "text" },
      { key: "cta_href", label: "Bottom CTA link target", type: "text" },
      {
        key: "feature_cards",
        label:
          "Feature cards (icon options: message-square, lightbulb, users, calendar)",
        type: "object_list",
        item_fields: [
          { key: "icon", label: "Icon", type: "select", options: ["message-square", "lightbulb", "users", "calendar"] },
          { key: "title", label: "Title", type: "text" },
          { key: "desc", label: "Description", type: "textarea" },
        ],
      },
    ],
  },

  api_docs_page: {
    label: "Developer / API Docs Page (composite)",
    description:
      "Renders the /developers page: hero (eyebrow, title, tagline), 4 capability cards, sample endpoints table, and a request-access box with a contact email.",
    defaults: {
      hero: { eyebrow: "Developer Docs", title: "", tagline: "" },
      capabilities: [],
      endpoints_heading: "Example endpoints",
      endpoints: [],
      access: { heading: "", body_prefix: "", email: "api@anterasoftware.com", body_suffix: "" },
    },
    fields: [
      {
        key: "hero",
        label: "Hero",
        type: "group",
        subfields: [
          { key: "eyebrow", label: "Eyebrow", type: "text" },
          { key: "title", label: "Title", type: "text" },
          { key: "tagline", label: "Tagline paragraph", type: "textarea" },
        ],
      },
      {
        key: "capabilities",
        label: "Capability cards",
        type: "object_list",
        item_fields: [
          { key: "icon",  label: "Icon", type: "select",
            options: ["database", "plug", "webhook", "key-round", "code", "book-open"] },
          { key: "title", label: "Card title", type: "text" },
          { key: "desc",  label: "Card description", type: "textarea" },
        ],
      },
      { key: "endpoints_heading", label: "Endpoints table heading", type: "text" },
      { key: "endpoints", label: "Sample endpoints", type: "object_list",
        item_fields: [
          { key: "method", label: "HTTP method", type: "select", options: ["GET", "POST", "PATCH", "DELETE"] },
          { key: "path", label: "Path (e.g. /api/orders)", type: "text" },
        ],
      },
      {
        key: "access",
        label: "Access box (contact info)",
        type: "group",
        subfields: [
          { key: "heading",     label: "Heading", type: "text" },
          { key: "body_prefix", label: "Body — text before email", type: "textarea" },
          { key: "email",       label: "Contact email", type: "text" },
          { key: "body_suffix", label: "Body — text after email", type: "textarea" },
        ],
      },
    ],
  },

  solutions_landing_page: {
    label: "Solutions Landing Page (composite)",
    description:
      "Renders /solutions: hero (prefix + accent + tagline), 6 alternating role sections each with icon/title/tagline/features/image/link, and a sky-600 bottom CTA. One block per page.",
    defaults: {
      hero: { title_prefix: "Solutions for every", title_accent: "role", tagline: "" },
      solutions: [],
      bottom_cta: { heading: "", subheading: "", primary_label: "Request a Demo", primary_href: "/contact", secondary_label: "Contact Sales", secondary_href: "/contact" },
    },
    fields: [
      {
        key: "hero",
        label: "Hero",
        type: "group",
        subfields: [
          { key: "title_prefix", label: "Title — lead words", type: "text" },
          { key: "title_accent", label: "Title — accent (sky-blue) word", type: "text" },
          { key: "tagline", label: "Tagline paragraph", type: "textarea" },
        ],
      },
      {
        key: "solutions",
        label: "Role sections (6 — alternating image/text rows)",
        type: "object_list",
        item_fields: [
          { key: "id",      label: "Slug/id (kebab-case)", type: "text" },
          { key: "icon",    label: "Icon", type: "select",
            options: ["store", "factory", "paintbrush", "printer", "pen-tool", "calculator"] },
          { key: "title",   label: "Section title", type: "text" },
          { key: "tagline", label: "Tagline paragraph", type: "textarea" },
          { key: "image",   label: "Section image", type: "image" },
          { key: "link",    label: "Deep-link route (e.g. /solutions/distributors)", type: "text" },
          { key: "features", label: "Feature bullets", type: "list" },
        ],
      },
      {
        key: "bottom_cta",
        label: "Bottom sky-600 CTA",
        type: "group",
        subfields: [
          { key: "heading",         label: "Heading", type: "text" },
          { key: "subheading",      label: "Subheading paragraph", type: "textarea" },
          { key: "primary_label",   label: "Primary button label", type: "text" },
          { key: "primary_href",    label: "Primary button link", type: "text" },
          { key: "secondary_label", label: "Secondary button label", type: "text" },
          { key: "secondary_href",  label: "Secondary button link", type: "text" },
        ],
      },
    ],
  },

  products_landing_page: {
    label: "Products Landing Page (composite)",
    description:
      "Renders /products: hero, 3 product sections (Advance, Aether, Integrations) each with icon/title/tagline/description/features grid/image/color, and a slate-900 bottom CTA. One block per page.",
    defaults: {
      hero: { title_prefix: "Our", title_accent: "Products", tagline: "" },
      products: [],
      bottom_cta: { heading: "", subheading: "", primary_label: "Request a Demo", primary_href: "/contact", secondary_label: "Contact Sales", secondary_href: "/contact" },
    },
    fields: [
      {
        key: "hero",
        label: "Hero",
        type: "group",
        subfields: [
          { key: "title_prefix", label: "Title — lead words", type: "text" },
          { key: "title_accent", label: "Title — accent (sky-blue) word", type: "text" },
          { key: "tagline", label: "Tagline paragraph", type: "textarea" },
        ],
      },
      {
        key: "products",
        label: "Product sections (3 — Advance / Aether / Integrations)",
        type: "object_list",
        item_fields: [
          { key: "id",          label: "Slug/id (kebab-case)", type: "text" },
          { key: "icon",        label: "Icon", type: "select",
            options: ["layers", "globe", "puzzle"] },
          { key: "title",       label: "Product title", type: "text" },
          { key: "href",        label: "Deep-link route (e.g. /advance)", type: "text" },
          { key: "color",       label: "Color palette", type: "select",
            options: ["sky", "indigo", "emerald"] },
          { key: "tagline",     label: "Tagline (one-liner)", type: "text" },
          { key: "description", label: "Full description paragraph", type: "textarea" },
          { key: "image",       label: "Product image", type: "image" },
          { key: "features",    label: "Feature bullets", type: "list" },
        ],
      },
      {
        key: "bottom_cta",
        label: "Bottom slate-900 CTA",
        type: "group",
        subfields: [
          { key: "heading",         label: "Heading", type: "text" },
          { key: "subheading",      label: "Subheading paragraph", type: "textarea" },
          { key: "primary_label",   label: "Primary button label", type: "text" },
          { key: "primary_href",    label: "Primary button link", type: "text" },
          { key: "secondary_label", label: "Secondary button label", type: "text" },
          { key: "secondary_href",  label: "Secondary button link", type: "text" },
        ],
      },
    ],
  },

  exchange_page: {
    label: "Exchange Page (composite)",
    description:
      "Renders /exchange: hero (logo + title + image + 2 CTAs), collaborative-events section with 3 icon-decorated bullet groups, elite-supplier section with 3 icon-decorated bullet groups, and a slate-900 bottom CTA.",
    defaults: {
      hero: {}, community: {}, elite: {}, bottom_cta: {},
    },
    fields: [
      {
        key: "hero",
        label: "Hero (logo + title + image + 2 CTAs)",
        type: "group",
        subfields: [
          { key: "logo_image",      label: "Left-side logo image", type: "image" },
          { key: "title_prefix",    label: "Title — lead words", type: "text" },
          { key: "title_accent",    label: "Title — accent (sky-blue) words", type: "text" },
          { key: "tagline",         label: "Tagline paragraph", type: "textarea" },
          { key: "primary_label",   label: "Primary button label", type: "text" },
          { key: "primary_href",    label: "Primary button link", type: "text" },
          { key: "secondary_label", label: "Secondary button label", type: "text" },
          { key: "secondary_href",  label: "Secondary button link", type: "text" },
          { key: "image",           label: "Right-side image", type: "image" },
        ],
      },
      {
        key: "community",
        label: "Community & Events section",
        type: "group",
        subfields: [
          { key: "image",      label: "Left-side image", type: "image" },
          { key: "heading",    label: "Heading", type: "text" },
          { key: "subheading", label: "Subheading paragraph", type: "textarea" },
          {
            key: "sections",
            label: "Bullet groups (3 — each with icon, title, and bullet list)",
            type: "object_list",
            item_fields: [
              { key: "icon",  label: "Icon", type: "select",
                options: ["users", "calendar", "message-square", "gift", "handshake", "award"] },
              { key: "title", label: "Group title", type: "text" },
              {
                key: "bullets",
                label: "Bullet points",
                type: "object_list",
                item_fields: [
                  { key: "lead", label: "Lead (bold text)", type: "text" },
                  { key: "body", label: "Body (regular text)", type: "textarea" },
                ],
              },
            ],
          },
          { key: "cta_label", label: "Section CTA label", type: "text" },
          { key: "cta_href",  label: "Section CTA link", type: "text" },
        ],
      },
      {
        key: "elite",
        label: "Elite Supplier Program section",
        type: "group",
        subfields: [
          { key: "image",           label: "Left-side image", type: "image" },
          { key: "heading",         label: "Heading", type: "text" },
          { key: "tagline",         label: "Tagline paragraph", type: "textarea" },
          {
            key: "sections",
            label: "Bullet groups (3 — each with icon, title, and bullet list)",
            type: "object_list",
            item_fields: [
              { key: "icon",  label: "Icon", type: "select",
                options: ["dollar-sign", "award", "shield-check", "target", "trending-up", "handshake"] },
              { key: "title", label: "Group title", type: "text" },
              {
                key: "bullets",
                label: "Bullet points",
                type: "object_list",
                item_fields: [
                  { key: "lead", label: "Lead (bold text)", type: "text" },
                  { key: "body", label: "Body (regular text)", type: "textarea" },
                ],
              },
            ],
          },
          { key: "primary_label",   label: "Primary button label", type: "text" },
          { key: "primary_href",    label: "Primary button link", type: "text" },
          { key: "secondary_label", label: "Secondary button label", type: "text" },
          { key: "secondary_href",  label: "Secondary button link", type: "text" },
        ],
      },
      {
        key: "bottom_cta",
        label: "Bottom slate-900 CTA",
        type: "group",
        subfields: [
          { key: "heading",      label: "Heading", type: "text" },
          { key: "subheading",   label: "Subheading paragraph", type: "textarea" },
          { key: "button_label", label: "Button label", type: "text" },
          { key: "button_href",  label: "Button link", type: "text" },
        ],
      },
    ],
  },

  elite_supplier_page: {
    label: "Elite Supplier Program Page (composite)",
    description:
      "Renders /exchange/elite-supplier: breadcrumb, gradient hero, 3 benefit sections (capabilities, distributor benefits, supplier benefits) each with a colored-check bullet list, a slate-900 closing statement with 2 CTAs, PartnerLogos band, and a related-supplier-content carousel.",
    defaults: {
      hero_eyebrow: "", hero_title_prefix: "", hero_title_accent: "", hero_tagline: "",
      breadcrumb_label: "Antera Exchange", breadcrumb_href: "/exchange",
      benefit_sections: [], closing: {}, related: {},
    },
    fields: [
      { key: "breadcrumb_label", label: "Breadcrumb label", type: "text" },
      { key: "breadcrumb_href", label: "Breadcrumb href", type: "text" },
      { key: "hero_eyebrow", label: "Hero eyebrow", type: "text" },
      { key: "hero_title_prefix", label: "Hero title prefix (before gradient text)", type: "text" },
      { key: "hero_title_accent", label: "Hero title accent (gradient text)", type: "text" },
      { key: "hero_tagline", label: "Hero tagline paragraph", type: "textarea" },
      {
        key: "benefit_sections",
        label: "Benefit sections (3 — capabilities, distributor benefits, supplier benefits)",
        type: "object_list",
        item_fields: [
          { key: "icon",   label: "Icon", type: "select",
            options: ["award", "users", "handshake"] },
          { key: "accent", label: "Accent bg-* class (e.g. bg-sky-600)", type: "text" },
          { key: "test_id_prefix", label: "Test-id prefix (kebab-case, e.g. elite-supplier-capability)", type: "text" },
          { key: "eyebrow", label: "Eyebrow", type: "text" },
          { key: "title",  label: "Section title", type: "text" },
          { key: "subtitle", label: "Subtitle paragraph", type: "textarea" },
          { key: "items",  label: "Bullet points", type: "list" },
        ],
      },
      {
        key: "closing",
        label: "Closing statement + 2 CTAs (slate-900 band)",
        type: "group",
        subfields: [
          { key: "lead",            label: "Lead sentence (before bold 1)", type: "textarea" },
          { key: "bold_1",          label: "Bold phrase 1", type: "text" },
          { key: "middle",          label: "Middle sentence (between bolds)", type: "textarea" },
          { key: "bold_2",          label: "Bold phrase 2", type: "text" },
          { key: "primary_label",   label: "Primary button label", type: "text" },
          { key: "primary_href",    label: "Primary button link", type: "text" },
          { key: "secondary_label", label: "Secondary button label", type: "text" },
          { key: "secondary_href",  label: "Secondary button link", type: "text" },
        ],
      },
      {
        key: "related",
        label: "Related carousel",
        type: "group",
        subfields: [
          { key: "tag",      label: "Tag filter", type: "text" },
          { key: "title",    label: "Carousel heading", type: "text" },
          { key: "subtitle", label: "Carousel subtitle", type: "text" },
        ],
      },
    ],
  },

  integrations_page: {
    label: "Integrations Page (composite)",
    description:
      "Renders /integrations: breadcrumb, hero, integrations hub graphic image, ecosystem card grid (6 categories, each with hierarchical items), dark utilities strip, sky-600 bottom CTA, and a related-content carousel.",
    defaults: {
      breadcrumb_label: "All Products", breadcrumb_href: "/products",
      hero_title: "Integrations", hero_tagline: "",
      hero_cta_label: "Request a Demo", hero_cta_href: "/contact",
      hero_image: "", hub_graphic: {}, ecosystem_heading: "Integration Ecosystem",
      ecosystem_subheading: "", ecosystem: [], utilities_heading: "Utilities",
      utilities_subheading: "", utilities: [], bottom_cta: {}, related: {},
    },
    fields: [
      { key: "breadcrumb_label", label: "Breadcrumb label", type: "text" },
      { key: "breadcrumb_href", label: "Breadcrumb href", type: "text" },
      { key: "hero_title", label: "Hero title", type: "text" },
      { key: "hero_tagline", label: "Hero tagline paragraph", type: "textarea" },
      { key: "hero_cta_label", label: "Hero CTA label", type: "text" },
      { key: "hero_cta_href", label: "Hero CTA href", type: "text" },
      { key: "hero_image", label: "Hero image", type: "image" },
      {
        key: "hub_graphic",
        label: "Hub graphic (image between hero and ecosystem grid)",
        type: "group",
        subfields: [
          { key: "src", label: "Image URL", type: "text" },
          { key: "alt", label: "Alt text (accessibility)", type: "text" },
        ],
      },
      { key: "ecosystem_heading", label: "Ecosystem section heading", type: "text" },
      { key: "ecosystem_subheading", label: "Ecosystem section subheading", type: "textarea" },
      {
        key: "ecosystem",
        label: "Ecosystem cards (6 categories — each with a title and list of tools)",
        type: "object_list",
        item_fields: [
          { key: "title", label: "Card title (e.g. Accounting, PromoStandards)", type: "text" },
          {
            key: "items",
            label: "Tools in this category (nested children are not editable — edit via JSON if needed)",
            type: "object_list",
            item_fields: [
              { key: "name",      label: "Tool name (e.g. QuickBooks Online)", type: "text" },
              { key: "available", label: "Available on Antera? (true / false)", type: "select",
                options: [{ value: "true", label: "true (available)" }, { value: "false", label: "false (coming soon)" }] },
            ],
          },
        ],
      },
      { key: "utilities_heading", label: "Utilities heading", type: "text" },
      { key: "utilities_subheading", label: "Utilities subheading", type: "text" },
      { key: "utilities", label: "Utilities list", type: "object_list",
        item_fields: [
          { key: "name", label: "Name", type: "text" },
          { key: "available", label: "Available (true/false)", type: "select",
            options: [{ value: "true", label: "true (available)" }, { value: "false", label: "false (coming soon)" }] },
        ]},
      {
        key: "bottom_cta",
        label: "Bottom sky-600 CTA",
        type: "group",
        subfields: [
          { key: "heading", label: "Heading", type: "text" },
          { key: "subheading", label: "Subheading paragraph", type: "textarea" },
          { key: "button_label", label: "Button label", type: "text" },
          { key: "button_href", label: "Button link", type: "text" },
        ],
      },
      {
        key: "related",
        label: "Related carousel",
        type: "group",
        subfields: [
          { key: "tag",      label: "Tag filter", type: "text" },
          { key: "title",    label: "Carousel heading", type: "text" },
          { key: "subtitle", label: "Carousel subtitle", type: "text" },
        ],
      },
    ],
  },

  academy_page: {
    label: "Academy Page (composite)",
    description:
      "Renders /academy: hero with animated stats, intro video, 3-goal grid, curated learning paths hub-and-spoke, 4-stage active learning engine, 6-card Antera Method, 3-card support system, and a sky-600 bottom CTA. Icons come from a whitelist (zap, layers, route, monitor, book-open, target, lightbulb, clock, dumbbell, bar-chart-3, compass, clapperboard, award, file-text, user-check, headphones).",
    defaults: {
      hero: {}, video: {}, goals: {}, paths: {}, engine: {},
      method: {}, support: {}, bottom_cta: {}, related: {},
    },
    fields: [
      {
        key: "hero",
        label: "Hero",
        type: "group",
        subfields: [
          { key: "eyebrow", label: "Eyebrow (small text above title)", type: "text" },
          { key: "title_lead", label: "Title lead (dark)", type: "text" },
          { key: "title_accent", label: "Title accent (sky-blue)", type: "text" },
          { key: "tagline", label: "Tagline paragraph", type: "textarea" },
          { key: "primary_label", label: "Primary CTA label", type: "text" },
          { key: "primary_href", label: "Primary CTA link", type: "text" },
          { key: "secondary_label", label: "Secondary CTA label", type: "text" },
          { key: "secondary_href", label: "Secondary CTA link", type: "text" },
          {
            key: "stats",
            label: "Stats (4 items — dark card grid)",
            type: "object_list",
            item_fields: [
              { key: "icon",  label: "Icon", type: "select",
                options: ["zap", "layers", "route", "monitor"] },
              { key: "value", label: "Value (e.g. 50x, 100+, 24/7)", type: "text" },
              { key: "label", label: "Label under the value", type: "text" },
            ],
          },
        ],
      },
      {
        key: "video",
        label: "Intro video",
        type: "group",
        subfields: [
          { key: "eyebrow", label: "Eyebrow", type: "text" },
          { key: "heading", label: "Heading", type: "text" },
          { key: "src", label: "Video URL (.mp4)", type: "text" },
          { key: "poster", label: "Poster image path", type: "text" },
        ],
      },
      {
        key: "goals",
        label: "Learning goals (3 cards)",
        type: "group",
        subfields: [
          { key: "eyebrow", label: "Eyebrow", type: "text" },
          { key: "heading", label: "Heading", type: "text" },
          {
            key: "items",
            label: "Goal cards",
            type: "object_list",
            item_fields: [
              { key: "icon",  label: "Icon", type: "select",
                options: ["book-open", "target", "lightbulb"] },
              { key: "title", label: "Card title", type: "text" },
              { key: "desc",  label: "Card description", type: "textarea" },
              { key: "color", label: "Tailwind bg-* class (e.g. bg-sky-500)", type: "text" },
            ],
          },
        ],
      },
      {
        key: "paths",
        label: "Curated Learning Paths (hub-and-spoke)",
        type: "group",
        subfields: [
          { key: "eyebrow", label: "Eyebrow", type: "text" },
          { key: "title_prefix", label: "Title — lead words", type: "text" },
          { key: "title_accent", label: "Title — accent (sky-blue) words", type: "text" },
          { key: "tagline", label: "Tagline paragraph", type: "textarea" },
          { key: "bullets", label: "Bullet points (below tagline)", type: "list" },
          { key: "center_label", label: "Center bubble label (e.g. You)", type: "text" },
          {
            key: "roles",
            label: "Satellite role cards (5 — pentagon positioning)",
            type: "object_list",
            item_fields: [
              { key: "label", label: "Role label", type: "text" },
              { key: "desc",  label: "Role description", type: "textarea" },
              { key: "top",   label: "Top position (%)", type: "text" },
              { key: "left",  label: "Left position (%)", type: "text" },
              { key: "delay", label: "Animation delay (seconds, e.g. 0.2)", type: "text" },
            ],
          },
        ],
      },
      {
        key: "engine",
        label: "Active Learning Engine (4 stages)",
        type: "group",
        subfields: [
          { key: "eyebrow", label: "Eyebrow", type: "text" },
          { key: "heading", label: "Heading", type: "text" },
          { key: "subheading", label: "Subheading", type: "textarea" },
          {
            key: "stages",
            label: "Stages",
            type: "object_list",
            item_fields: [
              { key: "num",   label: "Number (e.g. 01)", type: "text" },
              { key: "icon",  label: "Icon", type: "select",
                options: ["route", "clock", "dumbbell", "bar-chart-3"] },
              { key: "title", label: "Stage title", type: "text" },
              { key: "desc",  label: "Stage description", type: "textarea" },
            ],
          },
          { key: "loop_label", label: "Loop label (below stages, e.g. Repeat & Master)", type: "text" },
        ],
      },
      {
        key: "method",
        label: "The Antera Method (bento grid, 6 cards)",
        type: "group",
        subfields: [
          { key: "eyebrow", label: "Eyebrow", type: "text" },
          { key: "heading", label: "Heading", type: "text" },
          { key: "subheading", label: "Subheading", type: "textarea" },
          {
            key: "cards",
            label: "Cards",
            type: "object_list",
            item_fields: [
              { key: "icon",   label: "Icon", type: "select",
                options: ["compass", "clapperboard", "award", "dumbbell", "file-text", "target"] },
              { key: "title",  label: "Card title", type: "text" },
              { key: "desc",   label: "Card description", type: "textarea" },
              { key: "accent", label: "Tailwind border-l-* class (e.g. border-l-sky-500)", type: "text" },
            ],
          },
        ],
      },
      {
        key: "support",
        label: "Support system (3 cards)",
        type: "group",
        subfields: [
          { key: "eyebrow", label: "Eyebrow", type: "text" },
          { key: "heading", label: "Heading", type: "text" },
          {
            key: "items",
            label: "Support items",
            type: "object_list",
            item_fields: [
              { key: "icon",  label: "Icon", type: "select",
                options: ["user-check", "route", "headphones"] },
              { key: "title", label: "Item title", type: "text" },
              { key: "desc",  label: "Item description", type: "textarea" },
            ],
          },
        ],
      },
      {
        key: "bottom_cta",
        label: "Bottom sky-600 CTA",
        type: "group",
        subfields: [
          { key: "heading",         label: "Heading", type: "text" },
          { key: "subheading",      label: "Subheading paragraph", type: "textarea" },
          { key: "primary_label",   label: "Primary button label", type: "text" },
          { key: "primary_href",    label: "Primary button link", type: "text" },
          { key: "secondary_label", label: "Secondary button label", type: "text" },
          { key: "secondary_href",  label: "Secondary button link", type: "text" },
        ],
      },
      {
        key: "related",
        label: "Related carousel",
        type: "group",
        subfields: [
          { key: "tag",      label: "Tag filter", type: "text" },
          { key: "title",    label: "Carousel heading", type: "text" },
          { key: "subtitle", label: "Carousel subtitle", type: "text" },
        ],
      },
    ],
  },

  events_page: {
    label: "Events Page (composite)",
    description:
      "Renders /events: simple hero (accent-word title + tagline) followed by a related-content carousel of tagged blog posts. One block per page.",
    defaults: {
      title_prefix: "",
      title_accent: "Events",
      title_trail: "& Conferences",
      tagline: "",
      related: { tag: "Events", title: "Latest from Antera Events", subtitle: "" },
    },
    fields: [
      { key: "title_prefix", label: "Title — words before the accent (blank = hidden)", type: "text" },
      { key: "title_accent", label: "Title — accent (sky-600) word", type: "text" },
      { key: "title_trail", label: "Title — words after the accent", type: "text" },
      { key: "tagline", label: "Hero tagline paragraph", type: "textarea" },
      {
        key: "related",
        label: "Related carousel",
        type: "group",
        subfields: [
          { key: "tag",      label: "Tag filter", type: "text" },
          { key: "title",    label: "Carousel heading", type: "text" },
          { key: "subtitle", label: "Carousel subtitle", type: "text" },
        ],
      },
    ],
  },

  openhouse_page: {
    label: "Open House Page (composite)",
    description:
      "Renders /openhouse: hero image + accent title + tagline, a 3-card info strip (Date / Location / Who — icons position-locked to slot order: calendar, map-pin, users), and a Register-Interest CTA button.",
    defaults: {
      hero_image: "",
      hero_image_alt: "Antera Open House",
      title_lead: "Antera",
      title_accent: "Open House",
      tagline: "",
      info_cards: [],
      cta_label: "Register Interest",
      cta_href: "/contact",
    },
    fields: [
      { key: "hero_image", label: "Hero image (top of page, blank = hidden)", type: "image" },
      { key: "hero_image_alt", label: "Hero image alt text (accessibility)", type: "text" },
      { key: "title_lead", label: "Title — lead words (dark)", type: "text" },
      { key: "title_accent", label: "Title — accent (sky-600) words", type: "text" },
      { key: "tagline", label: "Hero tagline paragraph", type: "textarea" },
      {
        key: "info_cards",
        label: "Info cards (3 — icons position-locked: calendar / map-pin / users)",
        type: "object_list",
        item_fields: [
          { key: "icon", label: "Icon", type: "select", options: ["calendar", "map-pin", "users"] },
          { key: "title", label: "Card title", type: "text" },
          { key: "desc", label: "Card description", type: "text" },
        ],
      },
      { key: "cta_label", label: "Bottom CTA button label (blank = hidden)", type: "text" },
      { key: "cta_href", label: "Bottom CTA button link", type: "text" },
    ],
  },

  customer_portal_page: {
    label: "Customer Portal Page (composite)",
    description:
      "Renders /customer-portal: hero (lead + accent title + tagline), a 6-card feature grid, and a Request-Demo CTA button. Feature-card icons come from a whitelist (globe, file-text, credit-card, rotate-ccw, user-check, shield-check).",
    defaults: {
      title_lead: "Customer",
      title_accent: "Portal",
      tagline: "",
      features: [],
      cta_label: "Request a Demo",
      cta_href: "/contact",
    },
    fields: [
      { key: "title_lead", label: "Title — lead words (dark)", type: "text" },
      { key: "title_accent", label: "Title — accent (sky-600) words", type: "text" },
      { key: "tagline", label: "Hero tagline paragraph", type: "textarea" },
      {
        key: "features",
        label: "Feature cards (icon options: globe, file-text, credit-card, rotate-ccw, user-check, shield-check)",
        type: "object_list",
        item_fields: [
          { key: "icon", label: "Icon", type: "select", options: ["globe", "file-text", "credit-card", "rotate-ccw", "user-check", "shield-check"] },
          { key: "title", label: "Card title", type: "text" },
          { key: "desc", label: "Card description", type: "textarea" },
        ],
      },
      { key: "cta_label", label: "Bottom CTA button label (blank = hidden)", type: "text" },
      { key: "cta_href", label: "Bottom CTA button link", type: "text" },
    ],
  },
};

export const BLOCK_TYPES = Object.keys(BLOCK_SCHEMAS);

export function schemaFor(type) {
  return BLOCK_SCHEMAS[type];
}

export function labelFor(type) {
  return BLOCK_SCHEMAS[type]?.label || type;
}
